import {
  ICircularGraphData,
  IColorMap,
  TCircularGraphNameOverrides,
} from '@shared/components/circular-graph/circular-graph.interface';
import { IRankedEntity } from './admin-event-analytics.interface';

/** How many entries the Groups/Users tabs' stacked bar and circular graph visualizations show - always calculated client-side off whatever list they're given. */
export const EVENT_ANALYTICS_TOP_N = 5;

/**
 * `shikenso-purple-100` (darkest) down to its lightest `-24` tint - one color per rank, index 0
 * being the highest value. Used as Tailwind background classes for the stacked bar segments and
 * legend dots (real DOM elements, so a class reference is enough).
 */
export const EVENT_ANALYTICS_RANK_BAR_COLOR_CLASSES: string[] = [
  'bg-shikenso-purple-100',
  'bg-shikenso-purple-100-80',
  'bg-shikenso-purple-100-60',
  'bg-shikenso-purple-100-40',
  'bg-shikenso-purple-100-24',
];

/**
 * Same 5-step palette as `EVENT_ANALYTICS_RANK_BAR_COLOR_CLASSES`, but as literal hex values -
 * mirrors `--shikenso-purple-100[-N]` in `colors.scss`. ECharts renders to canvas, which can't
 * resolve CSS custom properties the way a DOM element's class can, so `app-circular-graph`'s
 * `colorMap` input needs actual color strings.
 */
export const EVENT_ANALYTICS_RANK_HEX_COLORS: string[] = [
  '#44084e',
  '#44084ecc',
  '#44084e99',
  '#44084e66',
  '#44084e3d',
];

/**
 * `app-circular-graph` only has 5 fixed data slots (originally meant for status/outcome
 * categories: processing/success/failed/problem/bonus) - repurposed here as 5 generic rank
 * buckets so the component can be reused for an arbitrary top-5 ranking instead of a status
 * breakdown. Order matters: it must match the slot order `CircularGraphService.buildMainSeriesData`
 * pushes in (processing, success, failed, problem, bonus) so segments render in rank order.
 */
const CIRCULAR_GRAPH_RANK_SLOTS: (keyof Omit<ICircularGraphData, 'total'>)[] = [
  'processing',
  'success',
  'failed',
  'problem',
  'bonus',
];

/** Colors the 5 rank buckets above with the same palette the stacked bars use, in rank order. */
export const EVENT_ANALYTICS_RANK_CIRCULAR_COLOR_MAP: Partial<IColorMap> = CIRCULAR_GRAPH_RANK_SLOTS.reduce(
  (colorMap, slot, index) => ({ ...colorMap, [slot]: EVENT_ANALYTICS_RANK_HEX_COLORS[index] }),
  {},
);

/**
 * Maps a (already top-5'd) ranking onto `app-circular-graph`'s `nameOverrides` input, in the same
 * rank order as `buildRankedCircularGraphData` - so hovering a ring segment shows the real entity
 * name (e.g. "Insight Agency") instead of the generic status label the shared component would
 * otherwise show for a repurposed slot.
 */
export function buildRankedCircularGraphNameOverrides(ranking: IRankedEntity[]): TCircularGraphNameOverrides {
  const overrides: TCircularGraphNameOverrides = {};

  ranking.forEach((entity, index) => {
    const slot = CIRCULAR_GRAPH_RANK_SLOTS[index];

    if (slot) {
      overrides[slot] = entity.name;
    }
  });

  return overrides;
}

/** Sorts descending by value (ties broken alphabetically ascending by name) and keeps the top `topN` entries. */
export function rankTopEntities(entities: IRankedEntity[], topN: number = EVENT_ANALYTICS_TOP_N): IRankedEntity[] {
  return [...entities].sort((a, b) => b.value - a.value || a.name.localeCompare(b.name)).slice(0, topN);
}

/**
 * Maps a (already top-5'd) ranking onto `app-circular-graph`'s 5 fixed data slots, in rank order.
 * `total` is set to the exact sum of the ranked values (not some larger grand total), so the ring
 * ends up fully divided among the ranked entries with no leftover "remaining" segment - matching
 * the design, which shows a complete ring for whatever the top-5 covers.
 */
export function buildRankedCircularGraphData(ranking: IRankedEntity[]): ICircularGraphData {
  const data: ICircularGraphData = { total: 0 };

  ranking.forEach((entity, index) => {
    const slot = CIRCULAR_GRAPH_RANK_SLOTS[index];

    if (slot) {
      data[slot] = entity.value;
      data.total = (data.total ?? 0) + entity.value;
    }
  });

  return data;
}
